import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useRecoveryStatus } from '@/hooks/useRecoveryStatus';
import { RecoveryBanner } from '@/components/recovery/RecoveryBanner';
import { RecoverySurveyModal } from '@/components/recovery/RecoverySurveyModal';
import { motion } from 'framer-motion';
import { BrandedLoading } from '@/components/common/BrandedLoading';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  User,
  Mail,
  BookOpen,
  Target,
  Trophy,
  Activity,
  Calendar,
  Briefcase,
  MessageCircle,
  ArrowRight,
  Sparkles,
  Pencil,
  CheckCircle2,
  CreditCard,
  Bell,
  Shield,
  Settings,
  ChevronRight,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { profileApi } from '@/services/profileApi';
import { userLearningPlanApi } from '@/services/learningManagementApi';
import { useProfileCompletionDetails } from '@/hooks/useProfile';
import { ProfilePictureUpload } from '@/components/profiles/ProfilePictureUpload';
import { ProfileCompletionDetails } from '@/components/profiles/ProfileCompletionDetails';
import { BioEditor } from '@/components/profiles/BioEditor';
import { useProfilePictureUrl } from '@/hooks/useProfilePictureUrl';
import type { ActivePlanDashboardResponse } from '@/types/learningManagement';
import type { UserProfile, UserProfileUpdateRequest } from '@/types/userProfile';
import { useToast } from '@/hooks/useToast';
import { EmailVerificationBanner } from '@/components/common/EmailVerificationBanner';
import { ActivityTimeline } from '@/components/profiles/ActivityTimeline';

/** Same key as LearningPlanPage / Coach / OAuthCallback: resolve email when auth user email not yet set. */
const LOCAL_USER_INFO_KEY = 'five_ai_local_user_info';

// Lightweight suggestion lists for the "dropdown-like" UX in the profile editor.
// Note: these are suggestions; users can still type any value.
const CITY_SUGGESTIONS = [
  'New York',
  'San Francisco',
  'Los Angeles',
  'Chicago',
  'Seattle',
  'Austin',
  'Boston',
  'London',
  'Berlin',
  'Toronto',
  'Vancouver',
  'Mumbai',
  'Bengaluru',
  'Hyderabad',
  'Delhi',
  'Singapore',
  'Sydney',
];

const COUNTRY_SUGGESTIONS = [
  'United States',
  'Canada',
  'United Kingdom',
  'Germany',
  'India',
  'Singapore',
  'Australia',
  'United Arab Emirates',
  'France',
  'Netherlands',
  'Sweden',
  'Japan',
  'South Korea',
  'Brazil',
  'Mexico',
  'Spain',
  'Italy',
  'Ireland',
  'Portugal',
  'New Zealand',
];

function getEmailFromStorage(): string | null {
  try {
    const raw = localStorage.getItem(LOCAL_USER_INFO_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed.signUpEmail ?? parsed.email ?? null;
  } catch {
    return null;
  }
}

/** Extract `exp` claim (ms) from JWT payload. Returns 0 on failure/unparseable. */
function getTokenExpMs(token: string): number {
  try {
    const base64Url = token.split('.')[1] || '';
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const jsonPayload = atob(padded);
    const expSeconds = JSON.parse(jsonPayload).exp;
    return typeof expSeconds === 'number' ? expSeconds * 1000 : 0;
  } catch {
    return 0;
  }
}

export const UserProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, isAuthenticated, isLoading: authLoading, accessToken } = useAuth();

  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [dashboardData, setDashboardData] = useState<ActivePlanDashboardResponse | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [editBioOpen, setEditBioOpen] = useState(false);
  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileForm, setProfileForm] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: 'PREFER_NOT_TO_SAY',
    phoneCountryCode: '+1',
    phoneNumber: '',
    city: '',
    state: '',
    country: '',
    postalCode: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
  });
  const { toast } = useToast();

  const email = user?.email ?? getEmailFromStorage() ?? '';
  const userId = profile?.userId ?? profile?.id ?? '';
  const { url: profilePictureUrl } = useProfilePictureUrl(profile?.profilePictureId);

  const { data: completionDetails, isLoading: completionLoading } = useProfileCompletionDetails(userId || undefined, !!userId);
  const { data: recoveryStatus, isLoading: recoveryLoading } = useRecoveryStatus(email || undefined, !!email);
  const [recoveryModalOpen, setRecoveryModalOpen] = useState(false);
  const [showFullBio, setShowFullBio] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated || !user) {
      // Check both React state token AND localStorage — on refresh, React state may not have flushed yet
      const storedAccess = accessToken || (typeof window !== 'undefined' ? localStorage.getItem('five_ai_access_token') : null);
      const storedRefresh = typeof window !== 'undefined' ? localStorage.getItem('five_ai_refresh_token') : null;

      if (storedAccess) {
        const expMs = getTokenExpMs(storedAccess);
        const now = Date.now();
        // Token not expired or unparseable — wait for AuthContext to finish hydrating
        if (expMs === 0 || expMs > now) return;
      }

      // Access token expired but refresh token exists — let AuthContext handle the refresh
      if (storedRefresh) return;

      navigate(`/login?returnUrl=${encodeURIComponent('/profile')}`, { replace: true });
    }
  }, [authLoading, isAuthenticated, user, accessToken, navigate]);

  const fetchProfile = useCallback(async () => {
    if (!email) return;
    setProfileLoading(true);
    try {
      const data = email.includes('@')
        ? await profileApi.getByEmail(email)
        : await profileApi.getById(email);
      setProfile(data);
      // Persist email from profile so it survives identity 403 on next load
      if (data?.email && typeof window !== 'undefined') {
        try {
          const raw = localStorage.getItem(LOCAL_USER_INFO_KEY);
          const parsed = raw ? (JSON.parse(raw) as Record<string, unknown>) : {};
          if (!parsed.signUpEmail && !parsed.email) {
            localStorage.setItem(LOCAL_USER_INFO_KEY, JSON.stringify({
              ...parsed,
              signUpEmail: data.email,
              email: data.email,
            }));
          }
        } catch {
          // ignore
        }
      }
    } catch {
      setProfile(null);
    } finally {
      setProfileLoading(false);
    }
  }, [email]);

  const fetchDashboard = useCallback(async () => {
    if (!email) return;
    setDashboardLoading(true);
    try {
      const data = await userLearningPlanApi.getDashboard(email);
      setDashboardData(data);
    } catch {
      setDashboardData(null);
    } finally {
      setDashboardLoading(false);
    }
  }, [email]);

  useEffect(() => {
    if (!email) return;
    fetchProfile();
    fetchDashboard();
  }, [email, fetchProfile, fetchDashboard]);

  useEffect(() => {
    if (!user?.email || typeof window === 'undefined') return;
    try {
      const stored = localStorage.getItem(LOCAL_USER_INFO_KEY);
      const parsed = stored ? JSON.parse(stored) : {};
      if (!parsed.signUpEmail && !parsed.email) {
        localStorage.setItem(LOCAL_USER_INFO_KEY, JSON.stringify({
          ...parsed,
          signUpEmail: user.email,
          email: user.email,
        }));
      }
    } catch {
      // ignore
    }
  }, [user?.email]);

  const handleProfilePictureSuccess = useCallback(() => {
    fetchProfile();
    queryClient.invalidateQueries({ queryKey: ['profile-picture-url'] });
    // Clear avatar cache so header picks up the new picture
    localStorage.removeItem('five_ai_avatar_url');
  }, [fetchProfile, queryClient]);

  const handleOpenEditProfile = useCallback(() => {
    if (!profile) return;
    setProfileForm({
      firstName: profile.firstName || '',
      lastName: profile.lastName || '',
      dateOfBirth: (profile.dateOfBirth || '').split('T')[0] || '',
      gender: profile.gender || 'PREFER_NOT_TO_SAY',
      phoneCountryCode: profile.phoneNumber?.countryCode || '+1',
      phoneNumber: profile.phoneNumber?.number || '',
      city: profile.location?.city || '',
      state: profile.location?.state || '',
      country: profile.location?.country || '',
      postalCode: profile.location?.postalCode || '',
      timezone: profile.location?.timezone || profile.preferences?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    });
    setEditProfileOpen(true);
  }, [profile]);

  const shouldOpenProfileEditorForField = useCallback((fieldName?: string) => {
    const n = (fieldName ?? '').toLowerCase();
    // Route "location/basic profile" fields to the Edit Profile dialog.
    return (
      n.includes('city') ||
      n.includes('state') ||
      n.includes('country') ||
      n.includes('postal') ||
      n.includes('timezone') ||
      n.includes('phone') ||
      n.includes('gender') ||
      n.includes('dob') ||
      n.includes('dateofbirth') ||
      n.includes('first') ||
      n.includes('last')
    );
  }, []);

  const openEditorForMissingField = useCallback(
    (fieldName?: string) => {
      if (shouldOpenProfileEditorForField(fieldName)) {
        handleOpenEditProfile();
        return;
      }
      setEditBioOpen(true);
    },
    [handleOpenEditProfile, shouldOpenProfileEditorForField]
  );

  const handleSaveProfile = useCallback(async () => {
    if (!profile?.userId) return;
    setProfileSaving(true);
    try {
      // PATCH — only send fields that have values.
      // Empty strings / blanks are omitted so the backend doesn't reject them.
      const payload: Record<string, unknown> = {};
      if (profileForm.firstName.trim()) payload.firstName = profileForm.firstName.trim();
      if (profileForm.lastName.trim()) payload.lastName = profileForm.lastName.trim();
      if (profileForm.dateOfBirth?.trim()) payload.dateOfBirth = profileForm.dateOfBirth.trim();
      if (profileForm.gender && profileForm.gender !== '') payload.gender = profileForm.gender;
      if (profileForm.phoneNumber.trim()) {
        payload.phoneNumber = {
          countryCode: profileForm.phoneCountryCode.trim() || '+1',
          number: profileForm.phoneNumber.trim(),
          verified: profile.phoneNumber?.verified ?? false,
        };
      }
      // Location — only include if at least one field has a value
      const loc: Record<string, string> = {};
      if (profileForm.city.trim()) loc.city = profileForm.city.trim();
      if (profileForm.state.trim()) loc.state = profileForm.state.trim();
      if (profileForm.country.trim()) loc.country = profileForm.country.trim();
      if (profileForm.postalCode.trim()) loc.postalCode = profileForm.postalCode.trim();
      if (profileForm.timezone.trim()) loc.timezone = profileForm.timezone.trim();
      if (Object.keys(loc).length > 0) payload.location = loc;

      await profileApi.update(profile.userId, payload as unknown as UserProfileUpdateRequest);
      await fetchProfile();
      queryClient.invalidateQueries({ queryKey: ['profiles', 'detail', profile.userId, 'completion-details'] });
      setEditProfileOpen(false);
      toast({ title: 'Profile updated', description: 'Your profile changes were saved.' });
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to update profile';
      const msg = String(message ?? '').toLowerCase();
      if ((msg.includes('payment') || msg.includes('checkout')) && typeof window !== 'undefined') {
        navigate('/checkout', { replace: true });
        return;
      }
      toast({ variant: 'destructive', title: 'Update failed', description: message });
    } finally {
      setProfileSaving(false);
    }
  }, [profile, profileForm, fetchProfile, queryClient, toast, navigate]);

  if (authLoading || !isAuthenticated || !user) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4">
        <BrandedLoading message="Completing authentication…" iconSize="md" />
      </div>
    );
  }

  const displayName =
    profile != null
      ? [profile.firstName, profile.lastName].filter(Boolean).join(' ') || profile.userName || profile.email
      : ([user.firstName, user.lastName].filter(Boolean).join(' ') || user.email?.split('@')[0] || email?.split('@')[0]) ?? 'You';

  const isLoading = profileLoading;
  const effectiveEmail = email || profile?.email || '';
  const hasEmail = !!effectiveEmail;
  const hasProfile = profile != null;
  const progress = dashboardData?.overallProgress ?? 0;
  const currentDay = dashboardData?.currentDayNumber ?? 1;
  const totalDays = dashboardData?.totalDays ?? 7;
  const daysCompleted = dashboardData?.daysCompleted ?? 0;
  const planTitle = dashboardData?.activePlan?.title ?? dashboardData?.learningPath?.title ?? 'Your plan';

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-background to-violet-500/[0.03] dark:to-violet-500/[0.06]">
      <div
        className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,hsl(var(--primary)/0.08),transparent_50%)] dark:bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,hsl(var(--primary)/0.12),transparent_50%)] pointer-events-none"
        aria-hidden
      />
      <Header />
      <main className="relative pt-20 pb-24 px-4 sm:px-6 min-w-0 overflow-x-hidden">
        <div className="max-w-4xl mx-auto space-y-8 w-full min-w-0">
          <EmailVerificationBanner email={effectiveEmail} emailVerified={profile?.emailVerified} />
          {/* Recovery banner for users with missing bio/learning path */}
          {recoveryStatus && recoveryStatus.recoveryActions.length > 0 && (
            <RecoveryBanner
              recoveryStatus={recoveryStatus}
              onStartRecovery={() => setRecoveryModalOpen(true)}
              isLoading={recoveryLoading}
            />
          )}
          <RecoverySurveyModal
            open={recoveryModalOpen}
            onOpenChange={setRecoveryModalOpen}
            userId={userId}
            existingSessionId={recoveryStatus?.hasSurveyData ? recoveryStatus.onboardingSessionId : null}
            source="PROFILE_PAGE"
            requiredQuestionIds={recoveryStatus?.requiredQuestionIds}
          />
          {/* Hero — catchy card with avatar, completion ring, and update CTAs */}
          <motion.section
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-3xl border-2 border-primary/10 bg-gradient-to-br from-violet-500/10 via-card to-fuchsia-500/10 dark:from-violet-500/20 dark:to-fuchsia-500/20 p-6 sm:p-8 shadow-xl shadow-primary/10"
          >
            <div className="flex flex-col sm:flex-row sm:items-center gap-8">
              <ProfilePictureUpload
                userId={userId}
                currentFileUrl={profilePictureUrl}
                displayName={displayName}
                onSuccess={handleProfilePictureSuccess}
                disabled={!userId}
                size="xl"
              />
              <div className="flex-1 min-w-0">
                <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground">
                  {displayName}
                </h1>
                {effectiveEmail && (
                  <p className="text-muted-foreground mt-1 flex items-center gap-2">
                    <Mail className="h-4 w-4 shrink-0" />
                    {effectiveEmail}
                  </p>
                )}
                {hasProfile && profile?.profileCompletionPercentage != null && (
                  <div className="mt-4 flex flex-wrap items-center gap-4">
                    <div className="flex-1 min-w-[140px]">
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-muted-foreground">Profile completion</span>
                        <span className="font-bold text-primary tabular-nums">{profile.profileCompletionPercentage}%</span>
                      </div>
                      <Progress value={profile.profileCompletionPercentage} className="h-2.5 rounded-full" />
                    </div>
                    {profile.profileCompletionPercentage < 100 && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-xl gap-2 shrink-0"
                        onClick={() => setEditBioOpen(true)}
                      >
                        <Pencil className="h-4 w-4" />
                        Complete profile
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-xl gap-2 shrink-0"
                      onClick={handleOpenEditProfile}
                      disabled={!hasProfile}
                    >
                      <Pencil className="h-4 w-4" />
                      Edit profile info
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </motion.section>

          {/* Profile completion breakdown + missing fields + Update option */}
          {hasProfile && userId && (
            <motion.section
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.03 }}
            >
              <Card className="rounded-2xl border-2 overflow-hidden shadow-md">
                <CardHeader className="pb-2">
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <CheckCircle2 className="h-5 w-5 text-primary" />
                        How complete is your profile?
                      </CardTitle>
                      <CardDescription>
                        {(profile?.profileCompletionPercentage ?? 0) >= 100
                          ? "Your profile is complete. You can still update anytime."
                          : "See what is done and what is missing. Update anytime."}
                      </CardDescription>
                    </div>
                    <Button
                      size="sm"
                      className="rounded-xl gap-2"
                      onClick={() => {
                        const missing = completionDetails?.missingFields ?? [];
                        const hasBasicMissing = missing.some((f) => shouldOpenProfileEditorForField(f.fieldName));
                        if (hasBasicMissing) handleOpenEditProfile();
                        else setEditBioOpen(true);
                      }}
                    >
                      <Pencil className="h-4 w-4" />
                      {(completionDetails?.missingFields?.length ?? 0) > 0 ? "Update missing fields" : "Update profile"}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {completionLoading ? (
                    <div className="py-8">
                      <BrandedLoading
                        message="Loading completion details…"
                        iconSize="sm"
                        layout="inline"
                      />
                    </div>
                  ) : completionDetails ? (
                    <ProfileCompletionDetails
                      profileId={userId}
                      details={completionDetails}
                      onEditMissingField={(fieldName) => openEditorForMissingField(fieldName)}
                    />
                  ) : (
                    <p className="text-sm text-muted-foreground py-4">
                      Completion breakdown will appear here. Complete your bio to improve your score.
                    </p>
                  )}
                </CardContent>
              </Card>
            </motion.section>
          )}

          {/* Account Management Section */}
          <motion.section
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.3 }}
          >
            <Card className="rounded-2xl border-2 overflow-hidden shadow-md">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5 text-primary" />
                  Account & Settings
                </CardTitle>
                <CardDescription>Manage your subscription, notifications, and privacy</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-2">
                  <button
                    type="button"
                    onClick={() => navigate('/billing')}
                    className="flex items-center justify-between w-full rounded-xl p-3 text-left hover:bg-muted/50 transition-colors group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
                        <CreditCard className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">Billing & Subscription</p>
                        <p className="text-xs text-muted-foreground">Manage plan, view invoices, payment methods</p>
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/settings/notifications')}
                    className="flex items-center justify-between w-full rounded-xl p-3 text-left hover:bg-muted/50 transition-colors group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
                        <Bell className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">Notification Preferences</p>
                        <p className="text-xs text-muted-foreground">Choose how and when you hear from us</p>
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/settings/privacy')}
                    className="flex items-center justify-between w-full rounded-xl p-3 text-left hover:bg-muted/50 transition-colors group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
                        <Shield className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">Privacy & Data</p>
                        <p className="text-xs text-muted-foreground">Export data, manage consent, delete account</p>
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                  </button>
                </div>
              </CardContent>
            </Card>
          </motion.section>

          {/* Edit bio dialog — refetch profile and completion when closed so updates are visible */}
          <Dialog
            open={editBioOpen}
            onOpenChange={(open) => {
              setEditBioOpen(open);
              if (!open && userId) {
                fetchProfile();
                queryClient.invalidateQueries({ queryKey: ['profiles', 'detail', userId, 'completion-details'] });
              }
            }}
          >
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Update your profile</DialogTitle>
                <DialogDescription>Add or edit your bio and missing fields to raise your completion score.</DialogDescription>
              </DialogHeader>
              {userId && (
                <BioEditor
                  key={`bio-${userId}-${profile?.updatedAt ?? '0'}`}
                  profileId={userId}
                  bio={profile?.bio}
                  readOnly={false}
                />
              )}
            </DialogContent>
          </Dialog>

          <Dialog open={editProfileOpen} onOpenChange={(open) => { if (!open && profileSaving) return; if (!open) { handleOpenEditProfile(); } setEditProfileOpen(open); }}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
              <DialogHeader>
                <DialogTitle>Edit profile information</DialogTitle>
                <DialogDescription>Update your basic profile details and contact information.</DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label>First name</Label>
                  <Input value={profileForm.firstName} onChange={(e) => setProfileForm((s) => ({ ...s, firstName: e.target.value }))} />
                </div>
                <div>
                  <Label>Last name</Label>
                  <Input value={profileForm.lastName} onChange={(e) => setProfileForm((s) => ({ ...s, lastName: e.target.value }))} />
                </div>
                <div>
                  <Label>Date of birth</Label>
                  <Input type="date" value={profileForm.dateOfBirth} onChange={(e) => setProfileForm((s) => ({ ...s, dateOfBirth: e.target.value }))} />
                </div>
                <div>
                  <Label>Gender</Label>
                  <Select value={profileForm.gender} onValueChange={(value) => setProfileForm((s) => ({ ...s, gender: value }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="MALE">MALE</SelectItem>
                      <SelectItem value="FEMALE">FEMALE</SelectItem>
                      <SelectItem value="NON_BINARY">NON_BINARY</SelectItem>
                      <SelectItem value="PREFER_NOT_TO_SAY">PREFER_NOT_TO_SAY</SelectItem>
                      <SelectItem value="OTHER">OTHER</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Phone country code</Label>
                  <Input value={profileForm.phoneCountryCode} onChange={(e) => setProfileForm((s) => ({ ...s, phoneCountryCode: e.target.value }))} />
                </div>
                <div>
                  <Label>Phone number</Label>
                  <Input value={profileForm.phoneNumber} onChange={(e) => setProfileForm((s) => ({ ...s, phoneNumber: e.target.value }))} />
                </div>
                <div>
                  <Label>City</Label>
                  <Input
                    value={profileForm.city}
                    onChange={(e) => setProfileForm((s) => ({ ...s, city: e.target.value }))}
                    list="city-options"
                  />
                </div>
                <div>
                  <Label>State</Label>
                  <Input value={profileForm.state} onChange={(e) => setProfileForm((s) => ({ ...s, state: e.target.value }))} />
                </div>
                <div>
                  <Label>Country</Label>
                  <Input
                    value={profileForm.country}
                    onChange={(e) => setProfileForm((s) => ({ ...s, country: e.target.value }))}
                    list="country-options"
                  />
                </div>
                <div>
                  <Label>Postal code</Label>
                  <Input value={profileForm.postalCode} onChange={(e) => setProfileForm((s) => ({ ...s, postalCode: e.target.value }))} />
                </div>
                <div className="md:col-span-2">
                  <Label>Timezone</Label>
                  <Input value={profileForm.timezone} onChange={(e) => setProfileForm((s) => ({ ...s, timezone: e.target.value }))} />
                </div>
              </div>
              <datalist id="city-options">
                {profileForm.city.trim() ? <option value={profileForm.city.trim()} /> : null}
                {CITY_SUGGESTIONS.map((opt) => (
                  <option key={opt} value={opt} />
                ))}
              </datalist>
              <datalist id="country-options">
                {profileForm.country.trim() ? <option value={profileForm.country.trim()} /> : null}
                {COUNTRY_SUGGESTIONS.map((opt) => (
                  <option key={opt} value={opt} />
                ))}
              </datalist>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => { handleOpenEditProfile(); setEditProfileOpen(false); }} disabled={profileSaving}>
                  Cancel
                </Button>
                <Button onClick={handleSaveProfile} disabled={profileSaving || !profile?.userId}>
                  {profileSaving ? 'Saving...' : 'Save changes'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {!hasEmail && (
            <Card className="rounded-2xl border-2 border-amber-500/30 bg-amber-500/5">
              <CardContent className="py-4">
                <p className="text-sm text-muted-foreground">
                  We couldn’t load your profile because your email wasn’t found. Try refreshing the page, or sign out and sign in again.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Bio */}
          <motion.section
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
          >
            <Card className="rounded-2xl border-2 overflow-hidden shadow-md">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5 text-primary" />
                  Bio
                </CardTitle>
                <CardDescription>Your summary, role, and goals</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {isLoading ? (
                  <div className="py-4">
                    <BrandedLoading message="Loading bio…" iconSize="sm" layout="inline" />
                  </div>
                ) : hasProfile && (profile.bio?.summary || profile.bio?.profileSummary || profile.bio?.currentRole) ? (
                  <>
                    {(profile.bio.summary || profile.bio.profileSummary) && (
                      <p className="text-sm text-foreground/90 leading-relaxed">
                        {profile.bio.summary || profile.bio.profileSummary}
                      </p>
                    )}
                    {(profile.bio.currentRole || profile.bio.currentCompany) && (
                      <div className="flex flex-wrap gap-2">
                        {profile.bio.currentRole && (
                          <Badge variant="secondary" className="gap-1 rounded-lg">
                            <Briefcase className="h-3.5 w-3.5" />
                            {profile.bio.currentRole}
                          </Badge>
                        )}
                        {profile.bio.currentCompany && (
                          <Badge variant="outline" className="rounded-lg">
                            {profile.bio.currentCompany}
                          </Badge>
                        )}
                        {profile.bio.yearsOfExperience != null && (
                          <Badge variant="outline" className="rounded-lg">
                            {profile.bio.yearsOfExperience}+ years
                          </Badge>
                        )}
                        {profile.bio.industry && (
                          <Badge variant="outline" className="rounded-lg">
                            {profile.bio.industry}
                          </Badge>
                        )}
                      </div>
                    )}

                    {/* View Full Bio toggle */}
                    {profile.bio.detailedBio && (
                      <div>
                        <button
                          type="button"
                          className="text-sm text-primary hover:underline font-medium mt-2 flex items-center gap-1"
                          onClick={() => setShowFullBio(!showFullBio)}
                        >
                          <BookOpen className="h-3.5 w-3.5" />
                          {showFullBio ? 'Show less' : 'View full bio'}
                        </button>
                        {showFullBio && (
                          <div className="mt-3 space-y-4 border-t pt-4">
                            <div className="text-sm text-foreground/85 leading-relaxed whitespace-pre-line">
                              {profile.bio.detailedBio}
                            </div>

                            {/* Skills */}
                            {profile.bio.skills && profile.bio.skills.length > 0 && (
                              <div>
                                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Skills</p>
                                <div className="flex flex-wrap gap-1.5">
                                  {profile.bio.skills.map((s: { skillName?: string; skillId?: string }, i: number) => (
                                    <Badge key={s.skillId || i} variant="secondary" className="rounded-md text-xs">
                                      {s.skillName || s.skillId}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Pain Points */}
                            {profile.bio.painPoints && profile.bio.painPoints.length > 0 && (
                              <div>
                                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Challenges</p>
                                <ul className="space-y-1">
                                  {profile.bio.painPoints.map((pp: { description?: string; painPointId?: string }, i: number) => (
                                    <li key={pp.painPointId || i} className="text-sm text-foreground/80 flex items-start gap-2">
                                      <span className="text-amber-500 mt-0.5">&#x2022;</span>
                                      {pp.description || pp.painPointId}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {/* Wins Looking For */}
                            {profile.bio.winsLookingFor && profile.bio.winsLookingFor.length > 0 && (
                              <div>
                                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Wins I'm Working Toward</p>
                                <ul className="space-y-1">
                                  {profile.bio.winsLookingFor.map((w: unknown, i: number) => {
                                    const win = w as { winDescription?: string; winId?: string } | string;
                                    const text = typeof win === 'string' ? win : (win.winDescription || win.winId || '');
                                    const key = typeof win === 'string' ? i : (win.winId || i);
                                    return (
                                      <li key={key} className="text-sm text-foreground/80 flex items-start gap-2">
                                        <span className="text-emerald-500 mt-0.5">&#x2713;</span>
                                        {text}
                                      </li>
                                    );
                                  })}
                                </ul>
                              </div>
                            )}

                            {/* Facts */}
                            {profile.bio.facts && profile.bio.facts.length > 0 && (
                              <div>
                                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Key Facts</p>
                                <ul className="space-y-1">
                                  {profile.bio.facts.map((fact: string, i: number) => (
                                    <li key={i} className="text-sm text-foreground/80 flex items-start gap-2">
                                      <span className="text-blue-500 mt-0.5">&#x2022;</span>
                                      {fact}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground py-2">
                    No bio yet. Complete onboarding or update your profile to add a summary and role.
                  </p>
                )}
              </CardContent>
            </Card>
          </motion.section>

          {/* Professional Goals */}
          {hasProfile && (() => {
            const bio = profile?.bio as Record<string, unknown> | undefined;
            const professionalGoals = (bio?.professionalGoals ?? bio?.professional_goals) as Array<Record<string, unknown>> | undefined;
            return professionalGoals && professionalGoals.length > 0 ? (
              <motion.section
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.07 }}
              >
                <Card className="rounded-2xl border-2 overflow-hidden shadow-md">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Target className="h-5 w-5 text-violet-500" />
                      Professional Goals
                    </CardTitle>
                    <CardDescription>Goals aligned with your learning journey</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {professionalGoals.map((goal, i) => {
                        const goalType = ((goal.type ?? goal.goalType ?? goal.goal_type ?? '') as string).toUpperCase();
                        const title = (goal.title ?? goal.goal_title ?? goal.name ?? goal.description ?? '') as string;
                        return title ? (
                          <div key={i} className="flex items-start gap-2 text-sm">
                            <span className={`px-1.5 py-0.5 text-xs rounded shrink-0 mt-0.5 ${
                              goalType === 'SHORT_TERM' || goalType === 'WEEKLY'
                                ? 'bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-400'
                                : 'bg-purple-50 text-purple-600 dark:bg-purple-500/10 dark:text-purple-400'
                            }`}>
                              {goalType === 'SHORT_TERM' || goalType === 'WEEKLY' ? 'Weekly' : 'Monthly'}
                            </span>
                            <span className="text-gray-700 dark:text-gray-300">{title}</span>
                          </div>
                        ) : null;
                      })}
                    </div>
                  </CardContent>
                </Card>
              </motion.section>
            ) : null;
          })()}

          {/* Learning status */}
          <motion.section
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="rounded-2xl border-2 overflow-hidden shadow-md bg-gradient-to-br from-primary/5 via-card to-violet-500/5 dark:from-primary/10 dark:to-violet-500/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5 text-primary" />
                  Learning status
                </CardTitle>
                <CardDescription>Your current plan and progress</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {dashboardLoading ? (
                  <div className="py-4">
                    <BrandedLoading message="Loading learning status…" iconSize="sm" layout="inline" />
                  </div>
                ) : dashboardData ? (
                  <>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-muted-foreground">Overall progress</span>
                      <span className="text-2xl font-bold text-primary tabular-nums">{Math.round(progress)}%</span>
                    </div>
                    <Progress value={progress} className="h-3" />
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="secondary" className="gap-1.5 rounded-lg">
                        <Calendar className="w-3.5 h-3.5" />
                        Day {currentDay} of {totalDays}
                      </Badge>
                      <Badge variant="outline" className="rounded-lg">
                        <Trophy className="w-3.5 h-3.5 mr-1" />
                        {daysCompleted} completed
                      </Badge>
                    </div>
                    {planTitle && (
                      <p className="text-sm text-muted-foreground">
                        Plan: <span className="font-medium text-foreground">{planTitle}</span>
                      </p>
                    )}
                    <div className="flex flex-wrap gap-3 pt-2">
                      <Button
                        size="sm"
                        className="rounded-xl gap-2"
                        onClick={() => navigate('/learning-plan')}
                      >
                        <BookOpen className="h-4 w-4" />
                        Open learning plan
                        <ArrowRight className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-xl gap-2"
                        onClick={() => navigate('/coach')}
                      >
                        <MessageCircle className="h-4 w-4" />
                        Open Coach
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="space-y-4">
                    <p className="text-sm text-muted-foreground">
                      No active learning plan yet. Complete onboarding to get a personalized plan.
                    </p>
                    <Button size="sm" className="rounded-xl gap-2" onClick={() => navigate('/onboarding')}>
                      <Sparkles className="h-4 w-4" />
                      Get started
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.section>

          {/* Activity */}
          <motion.section
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
          >
            <Card className="rounded-2xl border-2 overflow-hidden shadow-md">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5 text-primary" />
                  Activity
                </CardTitle>
                <CardDescription>Recent activity and engagement</CardDescription>
              </CardHeader>
              <CardContent>
                {userId ? (
                  <ActivityTimeline userId={userId} maxItems={20} />
                ) : (
                  <div className="flex flex-col items-center justify-center py-8 text-center">
                    <Activity className="h-12 w-12 text-muted-foreground/50 mb-3" />
                    <p className="text-sm text-muted-foreground font-medium">Activity coming soon</p>
                    <p className="text-xs text-muted-foreground mt-1 max-w-sm">
                      Logins, lesson completions, and engagement will appear here.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.section>
        </div>
      </main>
      <Footer />
    </div>
  );
};
